<script>
import CertificateGeneratorIconFeedback from './CertificateGeneratorIconFeedback.vue';
import CertificateGeneratorButton from './CertificateGeneratorButton.vue';

export default {
    name: 'CertificateGeneratorFormBulk',
    components: {
        CertificateGeneratorIconFeedback,
        CertificateGeneratorButton
    },
    props: {
        successfulGenerationMessage: { type: String, required: true },
        waitingGenerationMessage: { type: String, required: true },
        validateBulkUploadApi: { type: String, required: true },
        bulkGenerateCertificatesButtonText: { type: String, required: true },
        bulkVehicleDataText: { type: String, required: true },
        bulkVehicleDataHelpText: { type: String, required: true },
        bulkErrorGenerationMessage: { type: String, required: true },
        vinNumberText: { type: String, required: true },
        vehicleDataInvalidFormatError: { type: String, required: true },
        vehicleDataInvalidVinLengthError: { type: String, required: true },
        vehicleDataInvalidVinFormatError: { type: String, required: true },
        vehicleDataInvalidRegistrationLengthError: { type: String, required: true },
        vehicleDataInvalidRegistrationFormatError: { type: String, required: true },
    },
    emits: ['bulk-vin-numbers-validated'],
    data() {
        return {
            loading: false,
            error: false,
            successfullyAdded: false,
            bulkVehicleData: '',
            failedBulkVinRegistrations: [],
            validationError: '',
            parsedVinNumbers: [],
            parsedRegistrationNumbers: [],
        };
    },

    computed: {
        isResultsShown() {
            return this.loading || this.error || this.successfullyAdded;
        },
    },

    methods: {
        async validateBulkVehicleData() {
            if (!this.isValidInput()) return;
            
            this.resetPreValidatedState();
            await this.validate();
        },

        isValidInput() {
            if (this.bulkVehicleData.length < 8) return false;
            
            const entries = this.bulkVehicleData.trim().split('\n').filter(entry => entry.trim() !== '');
            
            this.parsedVinNumbers = [];
            this.parsedRegistrationNumbers = [];
            
            for (const entry of entries) {
                const trimmedEntry = entry.trim();
                
                let vin, registration = null;
                
                if (trimmedEntry.includes(';')) {
                    const parts = trimmedEntry.split(';');
                    if (parts.length !== 2) {
                        this.validationError = `${this.vinNumberText} ${trimmedEntry}: ${this.vehicleDataInvalidFormatError}`;
                        return false;
                    }
                    [vin, registration] = parts.map(part => part.trim());
                } else {
                    vin = trimmedEntry;
                }
                
                if (!this.isValidVin(vin, trimmedEntry)) {
                    return false;
                }
                
                if (registration !== null && !this.isValidRegistration(registration, trimmedEntry)) {
                    return false;
                }
                
                this.parsedVinNumbers.push(vin);
                const cleanRegistration = registration ? registration.replace(/\s+/g, '') : '';
                this.parsedRegistrationNumbers.push(cleanRegistration);
            }
            
            this.validationError = '';
            return true;
        },

        isValidVin(vin, fullEntry) {
            if (vin.length !== 8) {
                this.validationError = `${this.vinNumberText} ${fullEntry}: ${this.vehicleDataInvalidVinLengthError}`;
                return false;
            }
            
            // Check if VIN contains only alphanumeric characters
            if (!/^[A-Z0-9]+$/i.test(vin)) {
                this.validationError = `${this.vinNumberText} ${fullEntry}: ${this.vehicleDataInvalidVinFormatError}`;
                return false;
            }
            
            return true;
        },

        isValidRegistration(registration, fullEntry) {
            // Registration is optional, so empty is valid (even after semicolon)
            if (registration.length === 0) {
                return true;
            }
            
            // Check if registration contains only letters, numbers, and spaces
            if (!/^[A-Z0-9\s]+$/i.test(registration)) {
                this.validationError = `${this.vinNumberText} ${fullEntry}: ${this.vehicleDataInvalidRegistrationFormatError}`;
                return false;
            }
            
            // Remove spaces for length validation
            const cleanReg = registration.replace(/\s+/g, '');
            
            if (cleanReg.length < 2 || cleanReg.length > 8) {
                this.validationError = `${this.vinNumberText} ${fullEntry}: ${this.vehicleDataInvalidRegistrationLengthError}`;
                return false;
            }
            
            return true;
        },

        resetPreValidatedState() {
            this.loading = true;
            this.error = false;
            this.successfullyAdded = false;
            this.failedBulkVinRegistrations = [];
            this.validationError = '';
        },

        async validate() {
            try {
                const response = await this.validationRequest();
                if (this.hasInvalidVinNumbers(response)) {
                    this.handleInvalidVinNumbers(response);
                }
                if (response.inputValid) {
                    this.updateVinNumbers();
                }
            } catch (error) {
                this.error = true;
                console.error(error);
            } finally {
                this.loading = false;
            }
        },

        async validationRequest() {
            const vinOnlyData = this.parsedVinNumbers.join('\n');
            
            const response = await fetch(this.validateBulkUploadApi, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({bulkVinRegistrations: vinOnlyData})
            });
            if (!response.ok) {
                throw new Error('Something went wrong while validating the chassis number(s)', response.status);
            }
            return await response.json();
        },

        hasInvalidVinNumbers(response) {
            return response.failedVinRegistrations.length > 0;
        },

        handleInvalidVinNumbers(response) {
            this.failedBulkVinRegistrations = response.failedVinRegistrations;
            this.error = true;
        },

        updateVinNumbers() {
            this.error = false;
            this.successfullyAdded = true;
            
            const parsedData = {
                vinNumbers: this.parsedVinNumbers,
                registrationNumbers: this.parsedRegistrationNumbers
            };
            this.$emit('bulk-vin-numbers-validated', parsedData);
        },

        resetSuccessStatus() {
            this.error = false;
            this.successfullyAdded = false;
            this.validationError = '';
            this.parsedVinNumbers = [];
            this.parsedRegistrationNumbers = [];
        }
    },
};
</script>

<template>
    <form class="input-wrapper pt-3" @submit.prevent="validateBulkVehicleData">
        <label for="bulkVehicleData">{{ bulkVehicleDataText }}</label>
        <div class="search-block flex-column">
            <div class="search-block__input form-group position-relative">
                <textarea
                    id="bulkVehicleData"
                    v-model="bulkVehicleData"
                    name="bulkVehicleData"
                    class="form-control"
                    :class="{'is-invalid': error || validationError, 'is-valid': successfullyAdded}"
                    rows="6"
                    @input="resetSuccessStatus"
                >
                </textarea>
                <CertificateGeneratorIconFeedback v-if="successfullyAdded" :is-valid="true" />
                <CertificateGeneratorIconFeedback v-if="error || validationError" :is-valid="false" />
            </div>
            <CertificateGeneratorButton
                :text="bulkGenerateCertificatesButtonText"
                :disabled="bulkVehicleData.length < 8 || loading"
                :loading="loading"
            />
        </div>
        <div class="help-text-block">
            <p>{{ bulkVehicleDataHelpText }}</p>
        </div>
        <div
            v-if="isResultsShown || validationError"
            class="status-field"
            :class="{'is-invalid': error || validationError, 'is-valid': successfullyAdded}"
        >
            <p v-if="loading">{{ waitingGenerationMessage }}</p>
            <div v-else-if="validationError">
                <p>{{ validationError }}</p>
            </div>
            <div v-else-if="error">
                <p>{{ bulkErrorGenerationMessage }}</p>
                <ul v-if="failedBulkVinRegistrations.length > 0">
                    <li v-for="(failedVin, i) in failedBulkVinRegistrations" :key="`${failedVin}-${i}`">
                        {{ failedVin }}
                    </li>
                </ul>
            </div>
            <p v-else-if="successfullyAdded">{{ successfulGenerationMessage }}</p>
        </div>
    </form>
</template>
